import type { IconToken } from '../icons/index';
/** Things you can put inside your trailer, and the paint and carpet it comes in. */

export type InteriorSlotKind = 'wall' | 'floor';

export interface InteriorDef {
  id: string;
  name: string;
  icon: IconToken;
  price: number;
  /** Redneck Rating points while it's on display. */
  points: number;
  desc: string;
  slot: InteriorSlotKind;
  /** What it does for you, shown in the shop. */
  perk?: string;
  /** False for things you can only earn. */
  buyable: boolean;
}

export const INTERIOR: readonly InteriorDef[] = [
  {
    id: 'dogsPoker',
    name: 'Dogs Playing Poker (On Velvet)',
    icon: ':dogs-poker:',
    price: 35,
    points: 3,
    slot: 'wall',
    buyable: true,
    desc: 'The bulldog is cheating. Everybody knows. Nobody says anything.',
  },
  {
    id: 'wolfTapestry',
    name: 'Three Wolf Moon Tapestry',
    icon: ':wolf-tapestry:',
    price: 25,
    points: 3,
    slot: 'wall',
    buyable: true,
    desc: 'Howls at the moon so you don’t have to.',
  },
  {
    id: 'deerMount',
    name: 'Deer Head (His Name Is Gary)',
    icon: ':deer:',
    price: 60,
    points: 4,
    slot: 'wall',
    buyable: true,
    desc: 'Gary watches you eat cereal. Gary has seen things.',
  },
  {
    id: 'singingBass',
    name: 'Singing Bass Plaque',
    icon: ':singing-bass:',
    price: 30,
    points: 3,
    slot: 'wall',
    buyable: true,
    perk: 'Press its button for a song and a little clout.',
    desc: 'Motion activated. Your sanity is not.',
  },
  {
    id: 'neonBeer',
    name: 'Neon "COLD BEER" Sign',
    icon: ':neon-beer:',
    price: 45,
    points: 4,
    slot: 'wall',
    buyable: true,
    desc: 'Glows a romantic shade of bar.',
  },
  {
    id: 'dreamCatcher',
    name: 'Gas Station Dream Catcher',
    icon: ':dream-catcher:',
    price: 12,
    points: 2,
    slot: 'wall',
    buyable: true,
    desc: 'Catches dreams. Also dust. Mostly dust.',
  },
  {
    id: 'lazyBoy',
    name: 'Recliner With Cupholders',
    icon: ':recliner:',
    price: 80,
    points: 4,
    slot: 'floor',
    buyable: true,
    perk: 'A real nap spot: +25 energy.',
    desc: 'Vibrating massage feature broke in 2011. It still hums.',
  },
  {
    id: 'waterbed',
    name: 'King-Size Waterbed',
    icon: ':waterbed:',
    price: 150,
    points: 5,
    slot: 'floor',
    buyable: true,
    perk: 'Wake up with +15 energy and +10 health.',
    desc: 'Sleep like you’re at sea. Do not bring a cat in here.',
  },
  {
    id: 'beerFridge',
    name: 'Garage Beer Fridge',
    icon: ':beer-fridge:',
    price: 90,
    points: 4,
    slot: 'floor',
    buyable: true,
    perk: 'Somebody restocks it: one free cold one a day.',
    desc: 'It’s in the living room. That’s the garage now.',
  },
  {
    id: 'lavaLamp',
    name: 'Lava Lamp',
    icon: ':lava-lamp:',
    price: 15,
    points: 2,
    slot: 'floor',
    buyable: true,
    desc: 'Groovy. Hot enough to cook a hot dog on, if you’re patient.',
  },
  {
    id: 'fishTank',
    name: 'Fish Tank (One Survivor)',
    icon: ':fish-tank:',
    price: 40,
    points: 3,
    slot: 'floor',
    buyable: true,
    perk: 'Watching Kevin swim is free therapy: +energy.',
    desc: 'There were twelve fish. Now there is Kevin.',
  },
  {
    id: 'karaoke',
    name: 'Karaoke Machine',
    icon: ':microphone:',
    price: 75,
    points: 4,
    slot: 'floor',
    buyable: true,
    perk: 'Belt one out once a day for clout. The neighbors will hear.',
    desc: 'Comes with one disc. It is all Garth.',
  },
  {
    id: 'tanningBed',
    name: 'Used Tanning Bed',
    icon: ':tanning-bed:',
    price: 200,
    points: 6,
    slot: 'floor',
    buyable: true,
    perk: 'Fake bake once a day: +clout, a little -health.',
    desc: 'Bought off a salon that closed for "reasons."',
  },
  {
    id: 'gameConsole',
    name: 'Old Game Console & a CRT',
    icon: ':gamepad:',
    price: 50,
    points: 3,
    slot: 'floor',
    buyable: true,
    perk: 'Play a round for +energy.',
    desc: 'The memory card has one save on it and it’s yours.',
  },
  {
    id: 'canPyramid',
    name: 'Beer Can Pyramid',
    icon: ':can-pyramid:',
    price: 0,
    points: 5,
    slot: 'floor',
    buyable: false,
    desc: 'Earned, never bought. Fifty beers deep. A monument.',
  },
];

export const interiorDef = (id: string): InteriorDef | undefined => INTERIOR.find((d) => d.id === id);

export interface FinishDef {
  id: string;
  name: string;
  price: number;
}

/** Wall coverings. The first one is what you start with. */
export const WALLS: readonly FinishDef[] = [
  { id: 'paneling', name: 'Wood Paneling (Original)', price: 0 },
  { id: 'floral', name: 'Grandma’s Floral Wallpaper', price: 40 },
  { id: 'camo', name: 'Camo Everything', price: 60 },
  { id: 'leopard', name: 'Leopard Print Accent Walls', price: 60 },
  { id: 'racing', name: 'Checkered Flag Racing Theme', price: 50 },
];

/** Floor coverings. The first one is what you start with. */
export const FLOORS: readonly FinishDef[] = [
  { id: 'shag', name: 'Burnt Orange Shag (Original)', price: 0 },
  { id: 'avocado', name: 'Avocado Green Shag', price: 30 },
  { id: 'linoleum', name: 'Checkered Linoleum', price: 40 },
  { id: 'laminate', name: '"Hardwood" Laminate', price: 90 },
  { id: 'leopard', name: 'Leopard Carpet', price: 70 },
];

export const isWall = (id: string): boolean => WALLS.some((w) => w.id === id);
export const isFloor = (id: string): boolean => FLOORS.some((f) => f.id === id);

/** Beers it takes to earn the can pyramid. */
export const PYRAMID_BEERS = 50;

/** Redneck Rating from what's on display inside. */
export function interiorPoints(displayed: readonly string[]): number {
  return displayed.reduce((sum, id) => sum + (interiorDef(id)?.points ?? 0), 0);
}
